// Mirrors the homepage map vocabulary and intentionally excludes the catch-all
// "Other" value. Keep this POC-local so /hex does not depend on homepage state.
export const HEX_POC_SYMPTOMS = Object.freeze([
  "Cough/congestion",
  "Nausea",
  "Difficulty breathing",
  "Sore throat",
  "Rash",
  "Fever",
  "Chills",
  "Diarrhea",
  "Bleeding from body openings",
  "Red eyes",
  "Body aches",
  "Discolored or bloody urine",
  "Loss of smell or taste",
  "Yellow skin/eyes",
]);

const CANONICAL_SYMPTOM_BY_KEY = new Map(
  HEX_POC_SYMPTOMS.map((symptom) => [symptom.toLowerCase(), symptom]),
);

export function normalizeHexSymptomSelection(selectedSymptoms = []) {
  if (!Array.isArray(selectedSymptoms)) {
    throw new TypeError("Expected selected symptoms to be an array.");
  }
  const selected = new Set(selectedSymptoms.map((symptom, index) => {
    if (typeof symptom !== "string") {
      throw new TypeError(`Invalid selected symptom at index ${index}: expected a string.`);
    }
    const canonical = CANONICAL_SYMPTOM_BY_KEY.get(symptom.toLowerCase());
    if (!canonical) throw new RangeError(`Unknown selected symptom: ${symptom}.`);
    return canonical;
  }));
  return Object.freeze(HEX_POC_SYMPTOMS.filter((symptom) => selected.has(symptom)));
}

export function hexSymptomSelectionKey(selectedSymptoms = []) {
  return JSON.stringify(
    normalizeHexSymptomSelection(selectedSymptoms).map((symptom) => symptom.toLowerCase()),
  );
}

export function hexReportMatchesSymptoms(report, selectedSymptoms = []) {
  return reportMatchesNormalizedSymptoms(report, normalizeHexSymptomSelection(selectedSymptoms));
}

export function filterHexReportsBySymptoms(reports, selectedSymptoms = []) {
  if (!Array.isArray(reports)) throw new TypeError("Expected an array of human hex reports.");
  const normalizedSelection = normalizeHexSymptomSelection(selectedSymptoms);
  return reports.filter((report) => reportMatchesNormalizedSymptoms(report, normalizedSelection));
}

function reportMatchesNormalizedSymptoms(report, selectedSymptoms) {
  if (selectedSymptoms.length === 0) return true;
  if (report?.status !== "sick" || !Array.isArray(report.symptoms)) return false;
  const reportSymptoms = new Set(
    report.symptoms
      .filter((symptom) => typeof symptom === "string")
      .map((symptom) => symptom.toLowerCase()),
  );
  return selectedSymptoms.every((selected) => reportSymptoms.has(selected.toLowerCase()));
}
